import {
  type NilHundredResponseNil100FilterResponseRelatedPagedData,
  type PositionFilter,
  rdbV1FiltersTeamsList,
  rdbV2Nil100List,
  type TeamConferenceFilter,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { userAccessHeader } from '@on3/ui-lib/contexts/AuthProvider';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi, proxyApiWithVary } from '@on3/ui-lib/utils/api';
import { AdSidebarLayout } from 'components/Layouts/AdSidebarLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { Nil100PageComponent } from 'components/Nil/Rankings/Nil100/Nil100PageComponent';
import type { GetServerSideProps } from 'next';
import { usePathname } from 'next/navigation';
import { getFulfilledData } from 'utils/getFulfilledData';
import { createCanonicalUrl } from 'utils/meta';

export interface INil100PageProps extends IGenericPageProps {
  nilRankings: NilHundredResponseNil100FilterResponseRelatedPagedData;
  filters?: {
    teams?: TeamConferenceFilter[];
    positions?: PositionFilter[];
  };
}

const Nil100Page = (props: INil100PageProps) => {
  const pageTitle = 'On3 NIL Valuations';
  const description = `A ranking of top athletes based on NIL Valuation. These rankings are based on the potential influence they have as an individual.`;

  const noIndex = props?.nilRankings?.pagination?.count === 0;
  const pathname = usePathname();
  const canonical = createCanonicalUrl(pathname);

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      noIndex={noIndex}
      title={pageTitle}
    >
      <AdSidebarLayout>
        <Nil100PageComponent {...props} />
      </AdSidebarLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const nilSiteKey = 368;
  const siteData = await withValidUrl.isValid(ctx, nilSiteKey);
  const userData = await withAuthentication.isAuth(ctx);

  try {
    const accessLevel = userAccessHeader(userData);

    const params = {
      accessLevel,
      limit: 100,
    };
    const [nilRankings, teams] = await Promise.allSettled([
      rdbV2Nil100List(proxyApiWithVary, params),
      rdbV1FiltersTeamsList(proxyApi, {
        groupBy: true,
      }),
    ]);

    const data = {
      pageType: 'database',
      siteData,
      userData,
      nilRankings: getFulfilledData(nilRankings),
      filters: {
        teams: getFulfilledData(teams),
      },
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(Nil100Page);
